import React from 'react';
import { Modal, SplitContainer, Card, ContentCard, ToastContext } from '@weezevent/nacre';
import { CategoryListSelector } from '@weezevent/throne';
import ModalSummary from './ModalSummary/';
import InventorySwitch from './InventorySwitch';
import ModalMap from './ModalMap';
import { useTranslation } from 'react-i18next';
import { Availability } from '../../../../../../models';
import { Booking } from '../../../../../../models/bookingModel';
import useAppContext from '../../../../../../context/useAppContext';
import css from './manual-modal.module.css';
import '../../../../../../../src/styles/placement/dialog.scss';
import '../../../../../../../src/styles/placement/main.scss';

export default function ManualPlacementModal({
    unbookBooking = () => {},
    fullScreenMode,
    setFullScreenMode,
    inventories,
    previousInventory,
    setPreviousInventory = () => {},
    currentInventory,
    onChangeAllForm,
    selectedZone,
    setSelectedZone = () => {},
    previousZone,
    setPreviousZone = () => {},
    setCurrentInventory = () => {},
    backToSeats,
    rates = [],
    categories,
    form,
    onChange = () => {},
    onSelectRate = () => {},
    onConstructAttendees = () => {},
    onSetRates = () => {},
    onChangeSteps = () => {},
    step,
    timer
}) {
    const { t } = useTranslation();
    const [{ organization, event }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const [hoveredZone, setHoveredZone] = React.useState();
    const [selectedBuffer, setSelectedBuffer] = React.useState();
    const [popUp, setPopUp] = React.useState(false);
    const [showSeats, setShowSeats] = React.useState(false);
    const [preview, setPreview] = React.useState(true);
    const [loadingZones, setLoadingZones] = React.useState(false);
    const [isBooking, setIsBooking] = React.useState(false);

    const handleClose = React.useCallback(() => {
        setFullScreenMode(false);
        onChangeSteps(step + 2);
    }, [setFullScreenMode, step, onChangeSteps]);

    const handleClearAttendees = React.useCallback(() => {
        setFullScreenMode(false);
        unbookBooking(form.attendees);
    }, [setFullScreenMode, form, unbookBooking]);

    const isPreviewAvailable = React.useCallback(
        url => {
            // Check charts pictures availability
            let request = new XMLHttpRequest();
            request.open('GET', url, true);
            request.send();
            return (request.onload = function() {
                if (request.status !== 200) {
                    return setPreview(false);
                }
                return setPreview(true);
            });
        },
        [preview, setPreview]
    );

    const [availability, loading, sync] = Availability.useApiModel(
        {
            organizationId: organization?.id,
            eventId: event?.id,
            inventoryId: currentInventory,
            zoneId: selectedZone && selectedZone.uuid !== 'root' ? selectedZone.uuid : '',
            cache: false,
            onSuccess: zone => {
                setLoadingZones(true);
                isPreviewAvailable(zone?.preview_picture?.url);
                setSelectedBuffer(zone);
                onChange('selectedZone', selectedZone);
                onChange('previousZone', previousZone);

                if (currentInventory === previousInventory && !zone.__params.initiator && !showSeats) {
                    if (!zone?.children?.length && !backToSeats) {
                        setPopUp(true);
                    }
                    if (backToSeats) {
                        setPopUp(false);
                        setShowSeats(true);
                    }
                }
                setLoadingZones(false);
            }
        },
        [
            fullScreenMode,
            selectedZone,
            previousZone,
            event,
            organization,
            showSeats,
            isBooking,
            currentInventory,
            setLoadingZones,
            setSelectedBuffer,
            setPopUp,
            previousInventory,
            isPreviewAvailable
        ]
    );

    const catOptions = React.useMemo(() => {
        return categories
            .filter(cat => cat.rates.length >= 1)
            .map(category => {
                let tmpRates = [...category.rates];
                let tempContingents = [];

                // In Throne, to be able to sort the max price / min price,
                // you need to send the price as an integer.
                tmpRates.forEach(rate => {
                    return (rate.price = Number(rate.price));
                });

                if (availability && tmpRates.length) {
                    // GET RATES IN AVAILABILITY => CHILDREN => CATEGORIES => CONTINGENTS and get available quantity for each rates
                    availability.children.forEach(zone => {
                        zone.categories.forEach(catego => {
                            if (catego.contingents.length && catego.uuid === category.uuid) {
                                tempContingents.push(catego.contingents);

                                let ratesTab = [];
                                catego.contingents.forEach(cont => cont.rates.forEach(rate => ratesTab.push(rate)));

                                ratesTab.forEach(rate => {
                                    let tmpRate = tmpRates.find(ra => parseInt(ra.rate_obj.id) === parseInt(rate.id));
                                    if (tmpRate) {
                                        if (!Object.keys(tmpRate).includes('available_size')) {
                                            tmpRate.available_size = 0;
                                        }
                                        tmpRate.available_size += rate.available;
                                    }
                                });
                            }
                        });
                    });
                }

                return {
                    name: category.name,
                    uuid: category.uuid,
                    color: category.color,
                    rates: tmpRates,
                    contingent: tempContingents
                };
            });
    }, [availability, form, categories]);

    const onSelectZone = React.useCallback(
        zone => {
            // Set the new selected zone and the previous is also setted. Then we trigger a sync
            setSelectedZone(zone);
            setPreviousZone(selectedBuffer);
            setHoveredZone(null);
        },
        [setSelectedZone, setPreviousZone, selectedBuffer, setHoveredZone]
    );

    const onBackToRoot = React.useCallback(() => {
        setSelectedZone(null);
        setPreviousZone(null);
        setSelectedBuffer(null);
        setPopUp(false);
        setShowSeats(false);
        sync({
            organizationId: organization?.id,
            eventId: event.id,
            inventoryId: currentInventory,
            zoneId: '',
            initiator: true,
            cache: false
        });
    }, [setSelectedZone, setPopUp, onChange, inventories, setSelectedBuffer, setPreviousZone, setShowSeats, setCurrentInventory, setPreviousInventory, sync]);

    const onAddAttendee = React.useCallback(
        (autoSelect = true, seatsAndRates = []) => {
            // Get all categories because booking call must be donc for each category
            setIsBooking(true);
            let categories = [];
            let rates_obj = [];
            let rates_to_create = form.selected_rates
                .filter(rate => rate.inventory.uuid === currentInventory)
                .map(s_rate => {
                    // Get attendees already created for each rate
                    let attendees_already = form.attendees.filter(
                        attendee => attendee.rate_id === s_rate.rate.pk && attendee.sessionId === s_rate.sessionId && s_rate.inventory?.pk === attendee.booking_uuid?.inventory
                    );
                    if (attendees_already.length === s_rate.quantity) {
                        return null;
                    }

                    if (attendees_already.length > s_rate.quantity) {
                        toast.error(t('partners.invitation.download_ticket_error'));
                        return null;
                    }

                    return { ...s_rate, quantity: s_rate.quantity - attendees_already.length };
                })
                .filter(s_rate => s_rate);

            // Get rates objects
            if (autoSelect) {
                rates_obj = rates_to_create.map(s_rate => s_rate.rate);
            } else {
                seatsAndRates.forEach(seatAndRate => {
                    if (!rates_obj.includes(seatAndRate.rate)) {
                        rates_obj.push(seatAndRate.rate);
                    }
                });
            }

            // get category for each rate
            rates_obj.forEach(rate => {
                if (rate.resolved_category && !categories.includes(rate.resolved_category)) {
                    categories.push(rate.resolved_category);
                }
            });

            let bookingPromises = categories.map(cat => {
                let booking = new Booking();
                let seats = [];

                if (autoSelect) {
                    // On Automode, take selected rates
                    rates_to_create
                        .filter(s_rate => s_rate.rate.resolved_category === cat)
                        .forEach(s_rate => {
                            for (let i = 0; i < s_rate.quantity; i++) {
                                seats.push([s_rate.rate.pk]);
                            }
                        });
                } else {
                    // on Manual mode, get rate and seats from SeatsAndRates
                    seatsAndRates
                        .filter(seatAndRate => seatAndRate.rate.resolved_category === cat)
                        .forEach(seatAndRate => {
                            seats.push({ uuid: seatAndRate.seat.uuid, external_id: seatAndRate.rate.pk });
                        });
                }
                booking.seats = seats;
                booking.category = cat;
                return booking
                    .save({
                        organizationId: organization.pk,
                        chartId: event.chart,
                        inventoryId: currentInventory,
                        zoneId: selectedZone && selectedZone.uuid !== 'root' ? selectedZone?.uuid : '',
                        query: {
                            manual: !autoSelect
                        }
                    })
                    .then(({ response }) => {
                        if (response.success === true) {
                            setPopUp(false);
                        }
                        return response.seats;
                    })
                    .catch(e => {
                        toast.error(t(`partners.invitation.errors.${e.response.detail ? e.response.detail : 'unknownError'}`));
                    });
            });

            Promise.all(bookingPromises)
                .then(promises => {
                    // on error, we get undefined in promises, so we have to filter undefined
                    onConstructAttendees(promises.filter(promise => promise).flat());
                })
                .finally(() => {
                    setIsBooking(false);
                });
        },
        [onConstructAttendees, selectedZone, event, organization, form, setPopUp]
    );

    const renderInventories = React.useMemo(() => {
        if (inventories?.length === 1) {
            return <div className={css['single-inventory-title']}>{inventories[0].name}</div>;
        }

        return (
            <div className={css['modal-selector']}>
                <InventorySwitch
                    title={t('attendees.addAttendees.withSeats.modalSelector')}
                    inventories={inventories}
                    currentInventory={currentInventory}
                    previousInventory={previousInventory}
                    onChange={onChange}
                    showSeats={showSeats}
                    setCurrentInventory={setCurrentInventory}
                    setPreviousInventory={setPreviousInventory}
                />
            </div>
        );
    }, [inventories, currentInventory, setCurrentInventory]);

    return (
        <Modal className={css['swap-seat-modal']} size="fullscreen" open={fullScreenMode} onClose={handleClearAttendees} allowExternalClosing={false}>
            <SplitContainer
                className={css['modal__split-container']}
                additional={
                    <ModalSummary
                        handleClose={handleClose}
                        form={form}
                        onChange={onChange}
                        availability={availability}
                        sync={sync}
                        onChangeAllForm={onChangeAllForm}
                        selectedZone={selectedZone}
                        previousZone={previousZone}
                        categories={catOptions}
                        currentInventory={currentInventory}
                        setCurrentInventory={setCurrentInventory}
                        onAddAttendee={onAddAttendee}
                        inventories={inventories}
                        unbookBooking={unbookBooking}
                        timer={timer}
                    />
                }
            >
                <Card className={css['swap-seat-map-card']}>
                    <ContentCard>
                        {renderInventories}

                        <p>{t('ticket.availability.titleCategories')}:</p>

                        <div className={css['categories-container']}>{<CategoryListSelector categories={catOptions} />}</div>
                        <ModalMap
                            availability={availability}
                            currentInventory={currentInventory}
                            form={form}
                            categories={catOptions}
                            selectedZone={selectedZone}
                            setPreviousZone={setPreviousZone}
                            previousZone={previousZone}
                            popUp={popUp}
                            setPopUp={setPopUp}
                            inventories={inventories}
                            hoveredZone={hoveredZone}
                            showSeats={showSeats}
                            setShowSeats={setShowSeats}
                            onSelectZone={onSelectZone}
                            setHoveredZone={setHoveredZone}
                            loading={loading}
                            isBooking={isBooking}
                            loadingZones={loadingZones}
                            sync={sync}
                            preview={preview}
                            onBackToRoot={onBackToRoot}
                            currency={form.currency}
                            rates={rates}
                            onSelectRate={onSelectRate}
                            selectedRates={form.selected_rates}
                            onAddAttendee={onAddAttendee}
                            onSetRates={onSetRates}
                        />
                    </ContentCard>
                </Card>
            </SplitContainer>
        </Modal>
    );
}
